/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EmbryoTransferOutcome.module.css';
import { ReactComponent as EmbryoTransferProcedureImg } from '../../../assets/SVG/EmbryoTransferProcedure.svg';
import Grid from '@mui/material/Grid';
import { EmbryoTransfer, EmbryoTransferDetail, getEmbryoTransferDetails, getEmbryoTransferOutcome } from './EmbryoTransferOutcomeHelper';
import {  Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import EmbryoImageLightbox from '../../../pages/Embryology/Embryo_Culture/EmbryoImageLightbox';
import { convertUnderscoreToTitle } from '../../../utils/commonUtils';
  interface EmbryoTransferOutcomeProps {
    cycle: string;
    cycle_name?:string;
  }
  
const EmbryoTransferOutcome: React.FC<EmbryoTransferOutcomeProps> = ({ cycle, cycle_name }) => {
  const { t } = useTranslation('cycleOverview_config');
  const [OocyteRetrieval, setOocyteRetrievalData] = useState<EmbryoTransfer | null>(null);
  const [embryoTransferDetails, setEmbryoTransferDetails] = useState<EmbryoTransferDetail[] | null>(null);
  const [open, setOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null); 
  const [oocyteNumber, setOocyteNumber] = useState<string | number | null>(null);
  const [lightboxData, setLightboxData] = useState<{ imageUrl: string | null, label:string | null }>({ imageUrl: null, label:null });
  

  useEffect(() => {
    if (cycle) {
      getEmbryoTransferOutcome(cycle)
        .then((data) => {
          setOocyteRetrievalData(data);
        })
        .catch((error) => {
          console.error('Error fetching Oocyte Retrieval data:', error);
        });
    }
  }, [cycle]);



  useEffect(() => {
    getEmbryoTransferDetails(cycle)
      .then((response) => {
        setEmbryoTransferDetails(response); 
      })
      .catch((error) => {
        console.error('Error fetching Embryo Transfer Details:', error); 
      });
  }, [cycle]); 

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const openImagePreview = (src: string, oocyte: string | number) => {
    setImageSrc(src);
    setOocyteNumber(oocyte);
    setOpen(true);
  };

  const closeImagePreview = () => {
    setOpen(false);
    setImageSrc(null);
    setOocyteNumber(null);
  };



  return (
    <div className={styles.container}>

      {cycle_name !== 'IUI'&&(
        <Grid container spacing={3} alignItems="flex-start" className='mb-5' >
          <Grid item xs={12} >
            <span className={`${styles.Embryotext} `}>{t('EmbryoTransferDetails')}</span>
          </Grid>

          {/* Image Section */}
          <Grid item xs={12} md={4} className={styles.embryoProcedure}>
            <EmbryoTransferProcedureImg style={{ width: '20rem', height: '13.75rem' }} />
          </Grid>

          {/* Text Data Section */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('DateOfTransfer')}</strong>
                  <p className={styles.customTextStyle}>{OocyteRetrieval?.date || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('TimeOfTransfer')}</strong>
                  <p className={styles.customTextStyle}>{OocyteRetrieval?.start_time || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('Embryologist')}</strong>
                  <p className={styles.customTextStyle}>{convertUnderscoreToTitle(OocyteRetrieval?.witness_embryologist_name) || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('TransferMethod')}</strong>
                  <p className={styles.customTextStyle}>{convertUnderscoreToTitle(OocyteRetrieval?.transfer_method) || '-'}</p>
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('EndometriumThickness')}</strong>
                  <p className={styles.customTextStyle}>{OocyteRetrieval?.endometrium_thickness || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('EndometriumPattern')}</strong>
                  <p className={styles.customTextStyle}>{convertUnderscoreToTitle(OocyteRetrieval?.endometrium_pattern) || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('FreshTransfer')}</strong>
                  <p className={styles.customTextStyle}>{convertUnderscoreToTitle(OocyteRetrieval?.fresh_embryo_transfer_method) || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('FrozenTransfer')}</strong>
                  <p className={styles.customTextStyle}>
                    {OocyteRetrieval?.frozen_embryo_transfer_method === '0' ? '-' : OocyteRetrieval?.frozen_embryo_transfer_method || '-'}
                  </p>
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('Catheter')}</strong>
                  <p className={styles.customTextStyle}>{convertUnderscoreToTitle(OocyteRetrieval?.catheter) || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('TransferDepth')}</strong>
                  <p className={styles.customTextStyle}>{OocyteRetrieval?.transfer_depth || '-'}</p>
                </div>
              </Grid>
              <Grid item xs={6} sm={3} md={3}>
                <div className={styles.textField}>
                  <strong className={styles.headerTextStyle}>{t('UltrasoundMethod')}</strong>
                  <p className={styles.customTextStyle}>{convertUnderscoreToTitle(OocyteRetrieval?.ultrasound_method) || '-'}</p>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}


      {cycle_name !=='Surrogacy Cycle' && (

        <TableContainer className='mb-5'>
          <span className={`${styles.Embryotext} mt-4`}>{t('OutcomeDetails')}</span>
          <Table className='mt-2'>
            <TableHead>
              <TableRow>
                <TableCell className={styles.headerTextTable}><strong>{t('EmbryoNo')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('InseminationTechnique')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('CellStage')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('Day')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('Grade')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('Images')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('PGTResult')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('BetaHCG')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('USG')}</strong></TableCell>
                <TableCell className={styles.headerTextTable}><strong>{t('PregnancyOutcome')}</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {embryoTransferDetails && embryoTransferDetails.length > 0 ? (
                embryoTransferDetails.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell className={styles.headerTextTable}>{row.embryo_number}</TableCell>
                    <TableCell className={styles.headerTextTable}>{row.insemination_technique || '-'}</TableCell>
                    <TableCell className={styles.headerTextTable}>{convertUnderscoreToTitle(row.stage) || '-'}</TableCell>
                    <TableCell className={styles.headerTextTable}>{row.day}</TableCell>
                    <TableCell className={styles.headerTextTable}>{row.grade}</TableCell>
                    <TableCell className={styles.headerTextTable}>
                      {row.files && row.files.length > 0 ? (
                        <div className="d-flex flex-wrap justify-content-start" style={{ gap: '8px' }}>
                          {row.files.map((file: any, i: number) => (
                            <div key={i} className="d-flex align-items-center" style={{ gap: '6px' }}>
                              <img
                                style={{
                                  cursor: 'pointer',
                                  border: '2px solid #ccc',
                                  borderRadius: '8px',
                                  padding: '1px',
                                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                                  transition: 'transform 0.2s, box-shadow 0.2s',
                                }}
                                    
                                src={file.file}
                                alt={`file ${index}`}
                                className={styles.imageCell}
                                onClick={() => setLightboxData({ imageUrl: file.file!,  label:'' })}

                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>



                    <TableCell className={styles.headerTextTable}>
                      {row?.pgt_results?.pgt_results && row?.pgt_results?.pgt_result_embryo_sex
                        ? `${convertUnderscoreToTitle(row.pgt_results.pgt_results)} (${row.pgt_results.pgt_result_embryo_sex || '-'})`
                        : '-'}
                    </TableCell>

                    <TableCell className={styles.headerTextTable}>
                      {row.outcome && row.outcome[0].outcome_details ? (
                        <div style={{ color: '#A0A0A0', fontSize: '14px', lineHeight: '20px' }}>
                          <div>Value - {convertUnderscoreToTitle(row.outcome[0].outcome_details[0].status) || '-'}</div>
                          <div>Result - {row.outcome[0].outcome_details[0].result || '-'}</div>
                          <div>Date - {row.outcome[0].outcome_details[0].embryo_transfer_date ? row.outcome[0].outcome_details[0].embryo_transfer_date : '-'}</div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className={styles.headerTextTable}>
                      {row.outcome && row.outcome[0] ? (
                        <div style={{ color: '#A0A0A0', fontSize: '14px', lineHeight: '20px' }}>
                          <div>No of Sacs - {row.outcome[0].no_of_sacs || '-'}</div>
                          <div>Delivery type - {convertUnderscoreToTitle(row.outcome[0].delivery_type) || '-'}</div>
                          <div>Pregnacy type - {convertUnderscoreToTitle(row.outcome[0].pregnancy_type) || '-'}</div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className={styles.headerTextTable}>
                      <div style={{ color: '#A0A0A0', fontSize: '14px', lineHeight: '20px' }}>

                        <div>{row.outcome && row.outcome[0]?.birth_details?.length > 0 ? 'Yes' : (row.outcome ? 'No' : '-')}</div>
                        <div>{row.outcome && row.outcome[0]?.observation_date ? row.outcome[0]?.observation_date : ''}</div>
                        <div>{convertUnderscoreToTitle(row.outcome && row.outcome[0]?.birth_details[0]?.outcome_type ? row.outcome[0]?.birth_details[0]?.outcome_type : '')}</div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center"  className={styles.headerTextTable}>
                    {t('NoDataAvailable')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {lightboxData.imageUrl && (
        <EmbryoImageLightbox 
          imageUrl={lightboxData.imageUrl} 
          label= ''
          onClose={() => setLightboxData({ imageUrl: null,  label:null })} 
        />
      )}

      <Dialog
        open={open}
        onClose={closeImagePreview}
        maxWidth={false}
        sx={{
          width: '600px', 
          height: '600px', 
          margin: 'auto',
          '& .MuiPaper-root': { 
            width: '600px',
            height: '600px', 
            overflow: 'hidden', 
          },
        }}
      >
        <DialogTitle sx={{ position: 'relative' }}>
          {t('Embryo')} {oocyteNumber}
          <IconButton
            onClick={closeImagePreview}
            color="primary"
            sx={{
              position: 'absolute',
              top: 10,
              right: 4,
              padding: '8px',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100% - 56px)', 
            overflow: 'auto', 
          }}
        >
          {imageSrc && (
            <img
              src={imageSrc}
              alt="Large Preview"
              style={{
                maxWidth: '100%', 
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block', 
              }}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px', 
            justifyContent: 'center', 
          }}
        >
          <Button onClick={closeImagePreview} color="primary" sx={{ textTransform: 'capitalize' }}>
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EmbryoTransferOutcome;
